import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Aligned
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'; // Emergent
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'; // Misaligned
import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

// Reusable component to render a list of elements (aligned, misaligned, emergent)
const ElementList = ({ title, elements = [], icon, color }) => {
  if (!elements || elements.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        {React.cloneElement(icon, { color })}
        {title}
      </Typography>
      <Stack spacing={2.5}>
        {elements.map((item, index) => (
          <Box
            key={`${title.toLowerCase().replace(/\s+/g, '-')}-${index}`}
            sx={{
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {item.element}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.description}
              </Typography>
              {item.quotes?.length > 0 && (
                <Box sx={{ pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Supporting Quotes:
                  </Typography>
                  <List dense disablePadding>
                    {item.quotes.map((quote, idx) => (
                      <ListItem
                        key={idx}
                        disableGutters
                        sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                      >
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <FormatQuoteIcon
                            fontSize="small"
                            sx={{ transform: 'scaleX(-1)' }}
                            color="disabled"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={quote}
                          primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const TocAlignmentAnalysisTranscript = ({ summaryData }) => {
  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          ToC Alignment analysis data not available.
        </Box>
      </Card>
    );
  }

  const { summary, aligned_elements, misaligned_elements, emergent_elements } = summaryData.results;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Theory of Change (ToC) Alignment Analysis
          <AnalysisLensHelperText lensName="toc_alignment" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        {summary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Portfolio-Level Synthesis Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          </Box>
        )}

        {/* Aligned Elements */}
        <ElementList
          title="Aligned Elements (Supporting ToC/Model)"
          elements={aligned_elements}
          icon={<CheckCircleOutlineIcon />}
          color="success"
        />

        {/* Misaligned Elements */}
        <ElementList
          title="Misaligned Elements (Challenging ToC/Model)"
          elements={misaligned_elements}
          icon={<ReportProblemOutlinedIcon />}
          color="warning"
        />

        {/* Emergent Elements */}
        <ElementList
          title="Emergent Elements (New Insights)"
          elements={emergent_elements}
          icon={<LightbulbOutlinedIcon />}
          color="info"
        />

        <Typography variant="caption" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TocAlignmentAnalysisTranscript;
